import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { EditionDocumentationWorkflowStateViewStore } from "../../store";
import { EditionDocumentationWorkflowStateView } from "../view/EditionDocumentationWorkflowStateView";
import { EditionDocumentationWorkflowStateUpdateStore } from "../../store";
import { EditionDocumentationWorkflowStateUpdate } from "../update/EditionDocumentationWorkflowStateUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type EditionDocumentationWorkflowStateModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const EditionDocumentationWorkflowStateModalContent: React.FC<EditionDocumentationWorkflowStateModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new EditionDocumentationWorkflowStateUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new EditionDocumentationWorkflowStateViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <EditionDocumentationWorkflowStateView
            editionDocumentationWorkflowStateViewStore={
              storeInfo.store as EditionDocumentationWorkflowStateViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <EditionDocumentationWorkflowStateUpdate
            editionDocumentationWorkflowStateUpdateStore={
              storeInfo.store as EditionDocumentationWorkflowStateUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
